import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { Booking } from "@/lib/services/bookings";
import { formatETA } from "@/lib/utils/map-utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import {
  formatBookingDate,
  getBookingElapsedTime,
  getServiceSubtitle,
  getStatusBadge,
} from "./booking-card-utils";

interface BookingCardProps {
  booking: Booking;
  variant?: "list" | "detail";
  backgroundColor?: string;
  borderColor?: string;
  onPress?: () => void;
  onConfirm?: () => void;
}

export const BookingCard = React.memo(function BookingCard({
  booking,
  variant = "list",
  backgroundColor = BrandColors.white,
  borderColor = BrandColors.lightGray,
  onPress,
  onConfirm,
}: BookingCardProps) {
  const { theme } = useAuth();
  const statusBadge = getStatusBadge(booking.status);
  const showConfirmButton =
    variant === "list" &&
    (booking.status === "pending" || booking.status === "quoted");
  const accessibilityLabel = `Booking ${booking.id}. ${statusBadge.label}. ${getServiceSubtitle(booking)}. From ${booking.from_address ?? "unknown"} to ${booking.to_address ?? "unknown"}`;

  const content = (
    <>
      <View style={styles.cardHeader}>
        <Image
          source={require("@/assets/images/trip-estimate-truck.png")}
          style={styles.truckIcon}
          contentFit="contain"
        />
        <View style={styles.cardHeaderText}>
          <ThemedText style={styles.bookingRef} type="defaultSemiBold">
            #{booking.id}
          </ThemedText>
          <ThemedText
            style={styles.serviceSubtitle}
            type="extraSmall"
            numberOfLines={2}
          >
            {getServiceSubtitle(booking)}
          </ThemedText>
        </View>
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: statusBadge.backgroundColor },
          ]}
        >
          <View
            style={[styles.statusDot, { backgroundColor: statusBadge.color }]}
          />
          <ThemedText
            style={[styles.statusText, { color: statusBadge.color }]}
            type="extraSmallSemiBold"
          >
            {statusBadge.label}
          </ThemedText>
        </View>
      </View>

      <View style={styles.routeSection}>
        <View style={styles.routeIconColumn}>
          <View style={styles.pickupIconOuter}>
            <View style={styles.pickupIconInner} />
          </View>
          {booking.to_address ? (
            <View style={styles.routeDashedLine}>
              {Array.from({ length: 5 }).map((_, index) => (
                <View key={index} style={styles.routeDash} />
              ))}
            </View>
          ) : null}
          {booking.to_address ? (
            <Ionicons name="location" size={18} color="#E11D48" />
          ) : null}
        </View>

        <View style={styles.routeDetails}>
          <View style={styles.routeStop}>
            <ThemedText style={styles.pickupLabel} type="extraSmall">
              Pick Up
            </ThemedText>
            <ThemedText
              style={styles.routeAddress}
              type="smallSemiBold"
              numberOfLines={2}
            >
              {booking.from_address}
            </ThemedText>
          </View>

          {booking.to_address ? (
            <View style={styles.routeStop}>
              <ThemedText style={styles.destinationLabel} type="extraSmall">
                Destination
              </ThemedText>
              <ThemedText
                style={styles.routeAddress}
                type="smallSemiBold"
                numberOfLines={2}
              >
                {booking.to_address}
              </ThemedText>
            </View>
          ) : null}
        </View>
      </View>

      {variant === "detail" ? (
        <View style={styles.detailFooter}>
          <View style={styles.detailStatBox}>
            <Ionicons
              name="navigate-outline"
              size={16}
              color={BrandColors.primary}
            />
            <ThemedText style={styles.detailStatText} type="extraSmallSemiBold">
              {formatETA(booking.estimated_duration_minutes)}
            </ThemedText>
          </View>
          <View style={styles.detailStatBox}>
            <Ionicons
              name="calendar-outline"
              size={16}
              color={BrandColors.primary}
            />
            <ThemedText style={styles.detailStatText} type="extraSmallSemiBold">
              {formatBookingDate(booking.created_at)}
            </ThemedText>
          </View>
          <View style={styles.detailStatBox}>
            <Ionicons
              name="time-outline"
              size={16}
              color={BrandColors.primary}
            />
            <ThemedText style={styles.detailStatText} type="extraSmallSemiBold">
              {getBookingElapsedTime(booking)}
            </ThemedText>
          </View>
        </View>
      ) : (
        <View style={styles.cardFooter}>
          <View style={styles.footerSegment}>
            <Ionicons
              name="navigate-outline"
              size={16}
              color={BrandColors.primary}
            />
            <ThemedText style={styles.footerText} type="extraSmallSemiBold">
              {formatETA(booking.estimated_duration_minutes)}
            </ThemedText>
          </View>
          <View style={styles.footerDivider} />
          <View style={styles.footerSegment}>
            <Ionicons
              name="calendar-outline"
              size={16}
              color={BrandColors.primary}
            />
            <ThemedText style={styles.footerText} type="extraSmallSemiBold">
              {formatBookingDate(booking.created_at)}
            </ThemedText>
          </View>
          <View style={styles.footerDivider} />
          <View style={styles.footerSegment}>
            <ThemedText
              style={styles.viewDetailsText}
              type="extraSmallSemiBold"
            >
              View details
            </ThemedText>
            <Ionicons
              name="chevron-forward"
              size={14}
              color={BrandColors.primary}
            />
          </View>
        </View>
      )}
    </>
  );

  return (
    <View
      style={[
        styles.card,
        { backgroundColor, borderColor, shadowColor: theme.background },
        variant === "detail" && styles.detailCard,
      ]}
    >
      {onPress ? (
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={0.9}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
        >
          {content}
        </TouchableOpacity>
      ) : (
        content
      )}

      {showConfirmButton && onConfirm ? (
        <TouchableOpacity
          style={styles.confirmButton}
          onPress={onConfirm}
          activeOpacity={0.9}
          accessibilityRole="button"
          accessibilityLabel={`Confirm booking ${booking.id}`}
        >
          <ThemedText style={styles.confirmButtonText} type="smallSemiBold">
            Confirm
          </ThemedText>
          <Ionicons
            name="chevron-forward"
            size={16}
            color={BrandColors.white}
          />
        </TouchableOpacity>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
    borderWidth: 1,
    overflow: "hidden",
  },
  detailCard: {
    marginBottom: 0,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
  },
  truckIcon: {
    width: 40,
    height: 32,
  },
  cardHeaderText: {
    flex: 1,
    gap: 2,
  },
  bookingRef: {
    fontSize: 16,
    fontWeight: "700",
  },
  serviceSubtitle: {
    color: BrandColors.mediumGray,
    lineHeight: 16,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    flexShrink: 0,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
  },
  routeSection: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  routeIconColumn: {
    alignItems: "center",
    paddingTop: 2,
  },
  pickupIconOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupIconInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.white,
  },
  routeDashedLine: {
    alignItems: "center",
    gap: 3,
    marginVertical: 6,
  },
  routeDash: {
    width: 2,
    height: 5,
    borderRadius: 1,
    backgroundColor: BrandColors.lightGray,
  },
  routeDetails: {
    flex: 1,
    gap: 18,
  },
  routeStop: {
    gap: 4,
  },
  pickupLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  destinationLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  routeAddress: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 19,
  },
  cardFooter: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: BrandColors.lightGray,
    backgroundColor: "#F0FDF9",
    paddingVertical: 12,
  },
  footerSegment: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 4,
  },
  footerDivider: {
    width: 1,
    height: 20,
    backgroundColor: BrandColors.lightGray,
  },
  footerText: {
    color: BrandColors.charcoal,
    fontSize: 12,
  },
  viewDetailsText: {
    color: BrandColors.primary,
    fontSize: 12,
  },
  detailFooter: {
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  detailStatBox: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 6,
  },
  detailStatText: {
    color: BrandColors.charcoal,
    fontSize: 10,
    textAlign: "center",
  },
  confirmButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: BrandColors.primary,
    paddingVertical: 14,
  },
  confirmButtonText: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "600",
  },
});
